import React from "react";
import { cn } from "./cn";

/**
 * Bun radio option card. Spec: components/forms/RadioCard. The wizard's
 * single-choice pattern: full-width card, 1.5px border (blue when selected),
 * radio dot, title + optional muted helper. Stack with an 8px gap. Renders a
 * real radio input for keyboard/group semantics.
 */
export default function RadioCard({ checked, onChange, name, value, title, description, className, variant = 'default', disabled, ...props }) {
  const questionnaire = variant === 'questionnaire';
  const scale = variant === 'scale';
  if (scale) {
    return <label className={cn(
      'relative flex min-h-[52px] min-w-11 flex-1 items-center justify-center rounded-bun-lg border-[1.5px] font-instrument text-bun-metric font-semibold',
      checked ? 'border-bun-ink bg-bun-ink text-bun-on-ink' : 'border-bun-line bg-bun-surface text-bun-text hover:border-bun-line-strong',
      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer', className
    )}>
      <input {...props} type="radio" name={name} value={value} checked={checked} onChange={onChange} disabled={disabled}
        className="peer absolute inset-0 h-full w-full cursor-[inherit] opacity-0" />
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-bun-lg peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-bun-blue" />
      <span>{title}</span>
    </label>;
  }
  return (
    <label
      className={cn(
        "flex gap-[11px] font-instrument rounded-bun px-[14px] py-3 min-h-11",
        "border-[1.5px]",
        checked ? (questionnaire ? "border-bun-ink bg-bun-mist" : "border-bun-blue") : "border-bun-line hover:border-bun-line-strong",
        questionnaire && 'min-h-[52px] gap-[14px] rounded-bun-lg px-4',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        description ? "items-start" : "items-center",
        className
      )}
    >
      <span className={cn("relative inline-flex shrink-0", questionnaire ? 'w-5 h-5' : 'w-4 h-4', description && "mt-[1px]")}>
        <input
          {...props}
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="peer absolute inset-0 w-full h-full opacity-0 cursor-[inherit]"
        />
        <span
          aria-hidden="true"
          className={cn(
            "w-full h-full rounded-full box-border inline-flex items-center justify-center border-[1.5px]",
            checked ? (questionnaire ? 'border-bun-ink bg-bun-ink' : "border-bun-blue") : "border-bun-dim",
            "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-bun-blue"
          )}
        >
          {checked && <span className={cn("w-2 h-2 rounded-full", questionnaire ? 'bg-bun-on-ink' : 'bg-bun-blue')} />}
        </span>
      </span>
      <span className="min-w-0">
        <span className={cn("block text-bun-text", questionnaire ? 'text-bun-body' : 'text-[13.5px]', questionnaire && checked ? 'font-semibold' : 'font-strong')}>{title}</span>
        {description && (
          <span className="block text-bun-meta font-book text-bun-stone mt-[1px]">{description}</span>
        )}
      </span>
    </label>
  );
}
